/**
 * @Author: Anandp
 * @Date:   2024-07-25 06:04:10
 * @Last Modified by:   Eswari
 * @Last Modified Date: 2026-01-28
 */
import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import mtpDashboardResultsApi from "src/services/mtpDashboardApi";
import teamDashboardResultsApi from "src/services/teamdashboardApi";
import { useUser } from "src/hooks/use-user";
import CryptoJS from 'crypto-js';

const TeamContext = createContext();

export const TeamProvider = ({ children }) => {
  const navigate = useNavigate();
  const user = useUser();
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [isLoadingTeam, setIsLoading] = useState(false);
  const accessToken = sessionStorage.getItem("accessToken");
  const [selectedTeamName, setSelectedTeamName] = useState(null);
  const [clickWithOrg, setClickWithOrg] = useState(false);
  const [teamChangeState, setTeamChangeState] = useState(false);
  const [clickWithPage, setClickWithPage] = useState(false);
  const surveyPermsEn = sessionStorage.getItem("surveyPerms");
  const surveyPerms = CryptoJS.AES.decrypt(surveyPermsEn, user.id?.toString())?.toString(CryptoJS.enc.Utf8);

  const [teamsList, setTeamsList] = useState([]);
  const [noTeam, setNoTeam] = useState(false);
  const [isLoadingTeams, setIsLoadingTeams] = useState(true);

  const teamPermsEn = sessionStorage.getItem("teamPerms");
  const [teamPerms, setTeamPerms] = useState(() => {
    try {
      if (teamPermsEn && teamPermsEn !== "null" && user?.id) {
        const decryptedStr = CryptoJS.AES.decrypt(teamPermsEn, user.id.toString()).toString(CryptoJS.enc.Utf8);
        return decryptedStr ? JSON.parse(decryptedStr).map(Number) : [];
      }
    } catch (e) {
      console.error("Failed to decrypt initial teamPerms in provider:", e);
    }
    return [];
  });

  useEffect(() => {
    const fetchUsersTeams = async () => {
      if (!user?.id) {
        setIsLoadingTeams(false);
        return;
      }
      try {
        setIsLoadingTeams(true);
        const response = await teamDashboardResultsApi.getUsersTeams(
          accessToken,
          user.id
        );

        if (
          !response?.teamDetails?.userDetails ||
          response.teamDetails.userDetails.length === 0
        ) {
          setNoTeam(true);
        } else {
          const userDetailsList = response.teamDetails.userDetails;
          setTeamsList(userDetailsList);

          // Check if teamId is in query parameters
          const queryParams = new URLSearchParams(window.location.search);
          const urlTeamIdStr = queryParams.get("teamId");
          const urlTeamId = (urlTeamIdStr && !isNaN(Number(urlTeamIdStr)) && Number(urlTeamIdStr) > 0) ? Number(urlTeamIdStr) : null;
          
          const matchedTeam = urlTeamId ? userDetailsList.find(t => t.teamId === urlTeamId) : null;

          if (matchedTeam) {
            setSelectedTeamId(matchedTeam.teamId);
            setSelectedTeamName(matchedTeam.teamName);
            sessionStorage.setItem("teamDashboardTeamId", matchedTeam.teamId);
            sessionStorage.setItem("teamDashboardTeamName", matchedTeam.teamName);
            sessionStorage.setItem("teamSelectedTeam", matchedTeam.teamId);
          } else {
            const storedTeamIdStr = sessionStorage.getItem("teamDashboardTeamId");
            const storedTeamName = sessionStorage.getItem("teamDashboardTeamName");
            const storedTeamId = (storedTeamIdStr && storedTeamIdStr !== "undefined" && storedTeamIdStr !== "null" && !isNaN(Number(storedTeamIdStr)) && Number(storedTeamIdStr) > 0) ? Number(storedTeamIdStr) : null;
            if (storedTeamId && storedTeamName) {
              // Restore a previously selected team (e.g. page refresh on a dashboard page)
              setSelectedTeamId(storedTeamId);
              setSelectedTeamName(storedTeamName);
            }
            // If nothing is stored, leave selectedTeamId as null so the user
            // sees the /teamDashboard lock screen and must pick a team explicitly.
          }
        }
      } catch (error) {
        console.error("Error fetching users teams in provider:", error);
      } finally {
        setIsLoadingTeams(false);
      }
    };

    fetchUsersTeams();
  }, [user?.id, accessToken]);

  const setTeamName = (teamName) => {
    sessionStorage.setItem("teamDashboardTeamName", teamName);
    setSelectedTeamName(teamName);
  };
  const setTeamId = (teamId) => {
    const parsedId = (teamId && !isNaN(Number(teamId))) ? Number(teamId) : teamId;
    sessionStorage.setItem("teamSelectedTeam", parsedId);
    sessionStorage.setItem("teamDashboardTeamId", parsedId);
    setSelectedTeamId(parsedId);
  };
  const setIsLoadingTeam = (bool) => {
    setIsLoading(bool);
  };
  const setToClickWithOrg = (bool) => {
    setClickWithOrg(bool);
  };
  const seteamChange = (bool) => {
    setTeamChangeState(bool);
  };
  const setToClickWithPage = (bool) => {
    setClickWithPage(bool);
  };
  // eslint-disable-next-line
  const removeAllTeamIds = () => {
    setSelectedTeamId(null);
    sessionStorage.removeItem("teamSelectedTeam");
    sessionStorage.removeItem("teamDashboardTeamId");
    sessionStorage.removeItem("teamDashboardTeamName");
  };

  const updatePermission = async (id) => {
    setIsLoading(true);
    let teamId = sessionStorage.getItem("teamSelectedTeam");
    const numericId = Number(id);
    if (!isNaN(numericId) && numericId > 0) {
      let teamResponse = await mtpDashboardResultsApi.getTeamPermissions(
        accessToken,
        id,
        user.id
      );
      const permissions = teamResponse.data;
      
      // Filter out permission 18 if the team has no team-level dashboard access
      const hasTeamAccess = permissions.some(p => [5, 6, 7, 8, 9, 12].includes(p));
      const finalPermissions = hasTeamAccess ? permissions : permissions.filter(p => p !== 18);
      
      setTeamPerms(finalPermissions);
      
      if (teamChangeState) {
        seteamChange(false);
        // Use navigate() instead of window.location.href to avoid a full page
        // reload (which would remount the entire React tree, causing a second
        // API call and the visible double-render / double-loader effect).
        if (finalPermissions.includes(6)) {
          navigate('/teamDiversity');
        } else if (finalPermissions.includes(7)) {
          navigate('/clickWith?clickWithType=team');
        } else if (finalPermissions.includes(18) && user.tenetId !== 0) {
          navigate('/clickWith?clickWithType=org');
        } else if (finalPermissions.includes(8)) {
          navigate('/teamCompatability');
        } else if (finalPermissions.includes(5)) {
          navigate('/teamMojo');
        } else if (surveyPerms && surveyPerms.includes(5)) {
          navigate('/leadershipBrand');
        } else if (finalPermissions.includes(9)) {
          navigate('/teamDevelopment');
        } else {
          if (teamId && teamId?.length > 0) {
            navigate('/teamDiversity');
          } else {
            navigate('/teamDashboard');
          }
        }
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTeamId) {
      updatePermission(selectedTeamId);
    }
    // eslint-disable-next-line
  }, [selectedTeamId]);

  // Sync selectedTeamId to URL parameter if they differ (and user has a team)
  useEffect(() => {
    if (selectedTeamId && !isLoadingTeams && !noTeam) {
      const queryParams = new URLSearchParams(window.location.search);
      const urlTeamIdStr = queryParams.get("teamId");
      if (urlTeamIdStr !== String(selectedTeamId)) {
        queryParams.set("teamId", selectedTeamId);
        const newSearch = queryParams.toString();
        const newUrl = `${window.location.pathname}?${newSearch}`;
        window.history.replaceState(null, "", newUrl);
      }
    }
  }, [selectedTeamId, isLoadingTeams, noTeam]);

  return (
    <TeamContext.Provider
      value={{
        selectedTeamId,
        setTeamId,
        setTeamName,
        selectedTeamName,
        setToClickWithOrg,
        teamChangeState,
        seteamChange,
        isLoadingTeam,
        setIsLoadingTeam,
        clickWithOrg,
        setToClickWithPage,
        clickWithPage,
        removeAllTeamIds,
        teamsList,
        noTeam,
        isLoadingTeams,
        teamPerms,
      }}
    >
      {children}
    </TeamContext.Provider>
  );
};

export const useTeam = () => {
  return useContext(TeamContext);
};
